import React, { useMemo, useState } from 'react';
import { createRecordsBulk } from '../../../services/adminService';
import { AdminCard, AdminTableWrap, btnLink, btnPrimary, btnSecondary, inputClass } from './adminUi';
import { parseNumberInput, todayIso } from './adminShared';

export type QuickRow = {
  id: string;
  companyRoleName: string;
  source: string;
  addedDate: string;
  reliabilityScore: string;
  error?: string;
};

const emptyRow = (): QuickRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  companyRoleName: '',
  source: 'admin_bulk',
  addedDate: todayIso(),
  reliabilityScore: '0.8',
});

const columns: { key: keyof QuickRow; label: string; width?: number }[] = [
  { key: 'companyRoleName', label: 'Должность *', width: 220 },
  { key: 'addedDate', label: 'Дата данных', width: 130 },
  { key: 'source', label: 'Источник', width: 140 },
  { key: 'reliabilityScore', label: 'Надёжность', width: 90 },
];

export default function QuickAddGrid({
  companyId,
  onCancel,
  onSaved,
}: {
  companyId: number;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<QuickRow[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const validCount = useMemo(
    () => rows.filter((r) => r.companyRoleName.trim()).length,
    [rows],
  );

  const updateRow = (id: string, patch: Partial<QuickRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch, error: undefined } : row)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const copyFromAbove = (index: number) => {
    if (index <= 0) return;
    const source = rows[index - 1];
    updateRow(rows[index].id, {
      source: source.source,
      addedDate: source.addedDate,
      reliabilityScore: source.reliabilityScore,
    });
  };

  const fillDown = (index: number, field: keyof QuickRow) => {
    if (index <= 0) return;
    const value = rows[index - 1][field];
    setRows((prev) =>
      prev.map((row, i) => (i >= index ? { ...row, [field]: value, error: undefined } : row)),
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (index === rows.length - 1) addRow();
    }
  };

  const saveAll = async () => {
    setSaving(true);
    setMessage(null);
    const payload = rows
      .filter((r) => r.companyRoleName.trim())
      .map((r) => ({
        companyRoleName: r.companyRoleName.trim(),
        source: r.source.trim() || 'admin_bulk',
        addedDate: r.addedDate || todayIso(),
        reliabilityScore: parseNumberInput(r.reliabilityScore) ?? 0.8,
      }));

    if (payload.length === 0) {
      setMessage('Добавьте хотя бы одну строку с должностью');
      setSaving(false);
      return;
    }

    try {
      const result = await createRecordsBulk(companyId, payload);
      if (result.errors?.length) {
        const errorMap = new Map(result.errors.map((e) => [e.row - 1, e.error]));
        setRows((prev) => {
          const filled = prev.filter((r) => r.companyRoleName.trim());
          return filled.map((row, idx) => ({
            ...row,
            error: errorMap.get(idx),
          }));
        });
        setMessage(`Сохранено ${result.savedCount} из ${payload.length}. Исправьте строки с ошибками.`);
        if (result.savedCount > 0) onSaved();
      } else {
        setMessage(`Сохранено записей: ${result.savedCount}`);
        onSaved();
        onCancel();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminCard>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Быстрое добавление</h2>
          <p className="mt-1 text-sm text-dc-muted">Табличный ввод записей (шапка без компенсации). Enter — новая строка.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnSecondary} onClick={addRow}>Добавить строку</button>
          <button type="button" className={btnSecondary} onClick={onCancel}>Закрыть</button>
          <button type="button" className={btnPrimary} onClick={saveAll} disabled={saving || validCount === 0}>
            {saving ? 'Сохранение…' : `Сохранить (${validCount})`}
          </button>
        </div>
      </div>

      {message && (
        <div className="mb-3 rounded-lg bg-dc-primary-soft px-3 py-2 text-sm text-slate-600">{message}</div>
      )}

      <AdminTableWrap>
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/80 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-2 py-2">#</th>
              {columns.map((col) => (
                <th key={col.key} className="px-2 py-2" style={{ minWidth: col.width }}>{col.label}</th>
              ))}
              <th className="px-2 py-2">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <tr key={row.id} className={row.error ? 'bg-red-50/50' : undefined}>
                <td className="px-2 py-2">{index + 1}</td>
                <td className="px-2 py-2"><input className={inputClass} value={row.companyRoleName} onChange={(e) => updateRow(row.id, { companyRoleName: e.target.value })} onKeyDown={(e) => handleKeyDown(e, index)} placeholder="VP, Associate…" /></td>
                <td className="px-2 py-2">
                  <input className={inputClass} type="date" value={row.addedDate} onChange={(e) => updateRow(row.id, { addedDate: e.target.value })} />
                  <button type="button" className={`${btnLink} ml-1 text-xs`} onClick={() => fillDown(index, 'addedDate')}>↓</button>
                </td>
                <td className="px-2 py-2">
                  <input className={inputClass} value={row.source} onChange={(e) => updateRow(row.id, { source: e.target.value })} />
                  <button type="button" className={`${btnLink} ml-1 text-xs`} onClick={() => fillDown(index, 'source')}>↓</button>
                </td>
                <td className="px-2 py-2"><input className={inputClass} value={row.reliabilityScore} onChange={(e) => updateRow(row.id, { reliabilityScore: e.target.value })} /></td>
                <td className="whitespace-nowrap px-2 py-2">
                  <button type="button" className={btnLink} onClick={() => copyFromAbove(index)} disabled={index === 0}>Копировать ↑</button>
                  {' · '}
                  <button type="button" className={btnLink} onClick={() => removeRow(row.id)}>Удалить</button>
                  {row.error && <div className="text-xs text-red-600">{row.error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableWrap>
    </AdminCard>
  );
}
